import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cx } from '../utils.ts';
import { Button, IconButton } from './Button.tsx';
import './display.css';

const pad = (n: number) => String(n).padStart(2, '0');
export const toISODate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const fromISODate = (s: string | null | undefined): Date | null => {
  const m = s ? /^(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export interface CalendarProps {
  /** `YYYY-MM-DD` */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  weekStartsOn?: 0 | 1;
  className?: string;
}

export function Calendar({ value, onChange, weekStartsOn = 1, className }: CalendarProps) {
  const selected = fromISODate(value);
  const today = toISODate(new Date());
  const [cursor, setCursor] = useState(() => {
    const base = selected ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() - weekStartsOn + 7) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [cursor, weekStartsOn]);
  const weekdays = useMemo(() => {
    const base = new Date(2024, 0, 7 + weekStartsOn); // a Sunday + offset
    return Array.from({ length: 7 }, (_, i) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + i).toLocaleDateString('en-US', { weekday: 'narrow' }));
  }, [weekStartsOn]);
  const move = (delta: number) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div className={cx('g-calendar', className)}>
      <div className="g-calendar__head">
        <span className="g-calendar__title">{cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        <IconButton label="Previous month" size="xs" tooltip={false} onClick={() => move(-1)}>
          <ChevronLeft />
        </IconButton>
        <IconButton label="Next month" size="xs" tooltip={false} onClick={() => move(1)}>
          <ChevronRight />
        </IconButton>
      </div>
      <div className="g-calendar__grid" role="grid">
        {weekdays.map((w, i) => (
          <span key={i} className="g-calendar__wd">
            {w}
          </span>
        ))}
        {days.map((d) => {
          const iso = toISODate(d);
          return (
            <button
              key={iso}
              type="button"
              className="g-calendar__day"
              data-outside={d.getMonth() !== cursor.getMonth() || undefined}
              data-today={iso === today || undefined}
              aria-pressed={iso === value}
              onClick={() => onChange(iso)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="g-calendar__foot">
        <Button size="xs" variant="ghost" onClick={() => onChange(today)}>
          Today
        </Button>
        <Button size="xs" variant="ghost" onClick={() => onChange(null)} disabled={!value}>
          Clear
        </Button>
      </div>
    </div>
  );
}
